import React, { useState, useEffect } from 'react';
import {
  BarChart3, Building2, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, TrendingUp, Users, Calendar,
  Target, Activity, ArrowRight, Clock, Zap
} from 'lucide-react';
import { getAllOrganizations } from '../../services/superAdminService';
import { getActivityLogs } from '../../services/activityLogService';
import { getProjects, getTasks, getMembers } from '../../services/firestoreService';

// ============================================
// CLIENT KPI DASHBOARD — CLOSd
// ============================================
// Tracks 3 phases of client health:
//   Phase 1 — Activation (Days 1–7)
//   Phase 2 — Engagement (Weeks 1–3)
//   Phase 3 — Dependency (Weeks 4–6)
// ============================================

const ClientKPIDashboard = () => {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState(null);
  const [loadingKpis, setLoadingKpis] = useState(false);

  // Load all orgs on mount
  useEffect(() => {
    const loadOrgs = async () => {
      try {
        const allOrgs = await getAllOrganizations();
        setOrgs(allOrgs);
      } catch (err) {
        console.error('Error loading orgs:', err);
      }
      setLoading(false);
    };
    loadOrgs();
  }, []);

  // Load KPI data when an org is selected
  useEffect(() => {
    if (!selectedOrgId) {
      setKpiData(null);
      return;
    }
    const loadKpis = async () => {
      setLoadingKpis(true);
      try {
        const org = orgs.find(o => o.id === selectedOrgId);
        const [logs, projects, members] = await Promise.all([
          getActivityLogs(selectedOrgId),
          getProjects(selectedOrgId),
          getMembers(selectedOrgId),
        ]);

        // Load tasks for each project
        const projectsWithTasks = await Promise.all(
          projects.map(async (p) => {
            const tasks = await getTasks(selectedOrgId, p.id);
            return { ...p, tasks };
          })
        );

        const createdAt = org?.createdAt?.toDate
          ? org.createdAt.toDate()
          : new Date(org?.createdAt || Date.now());

        const data = computeKPIs(logs, projectsWithTasks, members, createdAt);
        setKpiData({ ...data, org, createdAt });
      } catch (err) {
        console.error('Error loading KPIs:', err);
      }
      setLoadingKpis(false);
    };
    loadKpis();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin" style={{ color: '#516469' }} />
        <span className="ml-2" style={{ color: '#516469' }}>Loading organizations...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#071D39' }}>Client KPI Tracking</h2>
          <p className="text-sm mt-1" style={{ color: '#516469' }}>
            Track activation, engagement, and dependency across all organizations
          </p>
        </div>
      </div>

      {/* Org Selector */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <label className="text-xs font-semibold block mb-2" style={{ color: '#89A8B1' }}>
          SELECT ORGANIZATION
        </label>
        <select
          value={selectedOrgId || ''}
          onChange={(e) => setSelectedOrgId(e.target.value || null)}
          className="w-full p-3 rounded-lg border text-sm font-semibold"
          style={{ borderColor: '#89A8B1', color: '#071D39', fontFamily: 'AvenirNext' }}
        >
          <option value="">— Choose an organization —</option>
          {orgs.map((org) => {
            const age = getOrgAgeDays(org);
            return (
              <option key={org.id} value={org.id}>
                {org.name} — {org.plan} — {age} days old
              </option>
            );
          })}
        </select>
      </div>

      {/* No org selected */}
      {!selectedOrgId && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4" style={{ color: '#89A8B1' }} />
          <p className="font-semibold" style={{ color: '#071D39' }}>Select an organization above</p>
          <p className="text-sm mt-1" style={{ color: '#516469' }}>
            Choose an org to view their activation, engagement, and dependency KPIs.
          </p>
        </div>
      )}

      {/* Loading KPIs */}
      {selectedOrgId && loadingKpis && (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-6 h-6 animate-spin" style={{ color: '#516469' }} />
          <span className="ml-2" style={{ color: '#516469' }}>Loading KPIs...</span>
        </div>
      )}

      {/* KPI Content */}
      {selectedOrgId && kpiData && !loadingKpis && (
        <KPIContent data={kpiData} />
      )}
    </div>
  );
};

// ============================================
// KPI CONTENT — All 3 phases
// ============================================

const KPIContent = ({ data }) => {
  const { phase, ageDays, org, createdAt } = data;
  const phaseLabel = phase === 1 ? 'Activation' : phase === 2 ? 'Engagement' : 'Dependency';
  const phaseColor = phase === 1 ? '#2B6CB0' : phase === 2 ? '#D69E2E' : '#75BB2E';

  return (
    <div>
      {/* Org Header + Phase Indicator */}
      <div className="bg-white rounded-lg shadow p-5 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold" style={{ color: '#071D39' }}>{org.name}</h3>
            <p className="text-sm" style={{ color: '#516469' }}>
              Plan: <span className="font-semibold">{org.plan}</span> · Created: {createdAt.toLocaleDateString()} · <span className="font-semibold">{ageDays} days old</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <HealthBadge data={data} />
            <div
              className="px-4 py-2 rounded-lg text-sm font-bold"
              style={{ backgroundColor: phaseColor + '15', color: phaseColor, border: `2px solid ${phaseColor}` }}
            >
              Phase {phase}: {phaseLabel}
            </div>
          </div>
        </div>

        {/* Phase Timeline */}
        <div className="flex items-center gap-0 mt-4">
          <PhaseTab num={1} label="Activation" days="Days 1–7" active={phase === 1} completed={phase > 1} color="#2B6CB0" />
          <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: '#89A8B1' }} />
          <PhaseTab num={2} label="Engagement" days="Weeks 1–3" active={phase === 2} completed={phase > 2} color="#D69E2E" />
          <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: '#89A8B1' }} />
          <PhaseTab num={3} label="Dependency" days="Weeks 4–6" active={phase === 3} completed={false} color="#75BB2E" />
        </div>
      </div>

      {/* Phase 1 — Activation */}
      <Phase1Card data={data} />

      {/* Phase 2 — Engagement */}
      <Phase2Card data={data} />

      {/* Phase 3 — Dependency */}
      <Phase3Card data={data} />
    </div>
  );
};

// ============================================
// PHASE 1 — ACTIVATION (Days 1–7)
// ============================================

const Phase1Card = ({ data }) => {
  const { phase1 } = data;
  const items = [
    { label: 'Account Created', met: phase1.accountCreated, icon: CheckCircle },
    {
      label: 'First Deal Created',
      met: phase1.firstDealCreated,
      icon: Target,
      sub: phase1.firstDealCreated
        ? (phase1.firstDealWithin48hrs
          ? '✅ Created within 48 hours'
          : '⚠️ Created after 48 hours')
        : null,
      warn: phase1.firstDealCreated && !phase1.firstDealWithin48hrs,
    },
    { label: 'First Checklist Triggered', met: phase1.firstChecklistTriggered, icon: Activity },
    { label: 'First Task Completed', met: phase1.firstTaskCompleted, icon: Zap },
    { label: 'First Document Deadline Entered', met: phase1.firstDueDateSet, icon: Calendar },
  ];

  const metCount = items.filter(i => i.met).length;
  const pct = Math.round((metCount / items.length) * 100);

  return (
    <div className="bg-white rounded-lg shadow p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg" style={{ backgroundColor: '#2B6CB015' }}>
            <Zap className="w-5 h-5" style={{ color: '#2B6CB0' }} />
          </div>
          <div>
            <h3 className="font-bold" style={{ color: '#071D39' }}>Phase 1 — Activation</h3>
            <p className="text-xs" style={{ color: '#516469' }}>Days 1–7 · First impressions matter</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold" style={{ color: pct === 100 ? '#75BB2E' : '#071D39' }}>
            {metCount}/{items.length}
          </p>
          <p className="text-xs" style={{ color: '#89A8B1' }}>{pct}% complete</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 rounded-full mb-4" style={{ backgroundColor: '#E2E8F0' }}>
        <div
          className="h-2 rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#75BB2E' : '#2B6CB0' }}
        />
      </div>

      {/* Checklist */}
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx}>
            <div className="flex items-center gap-3">
              {item.met ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#75BB2E' }} />
              ) : (
                <XCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#E53E3E' }} />
              )}
              <span
                className="text-sm font-semibold"
                style={{ color: item.met ? '#071D39' : '#E53E3E' }}
              >
                {item.label}
              </span>
            </div>
            {item.sub && (
              <p className="text-xs ml-8 mt-1" style={{ color: item.warn ? '#D69E2E' : '#75BB2E' }}>
                {item.sub}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// PHASE 2 — ENGAGEMENT (Weeks 1–3)
// ============================================

const Phase2Card = ({ data }) => {
  const { phase2, ageDays } = data;

  const metrics = [
    { label: 'Active Deals', value: phase2.activeDeals, target: '1+', met: phase2.activeDeals >= 1, icon: Target },
    { label: 'Tasks Completed', value: phase2.tasksCompleted, target: '5+', met: phase2.tasksCompleted >= 5, icon: CheckCircle },
    { label: 'Checklist Completion', value: `${phase2.checklistPct}%`, target: 'Progressing', met: phase2.checklistPct > 0, icon: BarChart3 },
    { label: 'Days Active This Week', value: phase2.daysActiveThisWeek, target: '3+', met: phase2.daysActiveThisWeek >= 3, icon: Calendar },
    { label: 'Seats Added', value: phase2.seatsAdded, target: 'Any', met: phase2.seatsAdded > 0, icon: Users },
  ];

  // Day 14 checkpoint
  const day14Met = phase2.activeDeals >= 1 && phase2.tasksCompleted >= 5;
  const showDay14Alert = ageDays >= 14 && !day14Met;

  return (
    <div className="bg-white rounded-lg shadow p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg" style={{ backgroundColor: '#D69E2E15' }}>
            <TrendingUp className="w-5 h-5" style={{ color: '#D69E2E' }} />
          </div>
          <div>
            <h3 className="font-bold" style={{ color: '#071D39' }}>Phase 2 — Engagement</h3>
            <p className="text-xs" style={{ color: '#516469' }}>Weeks 1–3 · Building the habit</p>
          </div>
        </div>
      </div>

      {/* Day 14 Checkpoint Alert */}
      {showDay14Alert && (
        <div
          className="flex items-center gap-3 p-3 rounded-lg mb-4"
          style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }}
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: '#DC2626' }} />
          <div>
            <p className="text-sm font-bold" style={{ color: '#DC2626' }}>
              🚨 Day 14 Checkpoint MISSED
            </p>
            <p className="text-xs" style={{ color: '#DC2626' }}>
              This org should have ≥1 active deal AND ≥5 tasks completed by Day 14. They have {phase2.activeDeals} deal(s) and {phase2.tasksCompleted} task(s) completed.
            </p>
          </div>
        </div>
      )}

      {ageDays < 14 && (
        <div
          className="flex items-center gap-3 p-3 rounded-lg mb-4"
          style={{ backgroundColor: '#EBF8FF', border: '1px solid #BEE3F8' }}
        >
          <Clock className="w-5 h-5 flex-shrink-0" style={{ color: '#2B6CB0' }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#2B6CB0' }}>
              Day 14 Checkpoint in {14 - ageDays} day(s)
            </p>
            <p className="text-xs" style={{ color: '#2B6CB0' }}>
              Target: ≥1 active deal AND ≥5 tasks completed
            </p>
          </div>
        </div>
      )}

      {ageDays >= 14 && day14Met && (
        <div
          className="flex items-center gap-3 p-3 rounded-lg mb-4"
          style={{ backgroundColor: '#F0FFF4', border: '1px solid #C6F6D5' }}
        >
          <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#38A169' }} />
          <p className="text-sm font-bold" style={{ color: '#38A169' }}>
            ✅ Day 14 Checkpoint PASSED
          </p>
        </div>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-3">
        {metrics.map((m, idx) => {
          const Icon = m.icon;
          return (
            <div
              key={idx}
              className="p-3 rounded-lg"
              style={{
                backgroundColor: m.met ? '#F0FFF4' : '#FFF5F5',
                border: `1px solid ${m.met ? '#C6F6D5' : '#FED7D7'}`,
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" style={{ color: m.met ? '#38A169' : '#E53E3E' }} />
                <span className="text-xs font-semibold" style={{ color: '#516469' }}>{m.label}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xl font-bold" style={{ color: '#071D39' }}>{m.value}</span>
                <span className="text-xs px-2 py-0.5 rounded" style={{
                  backgroundColor: m.met ? '#C6F6D5' : '#FED7D7',
                  color: m.met ? '#38A169' : '#E53E3E',
                }}>
                  Target: {m.target}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// PHASE 3 — DEPENDENCY (Weeks 4–6)
// ============================================

const Phase3Card = ({ data }) => {
  const { phase3, ageDays } = data;

  const metrics = [
    { label: 'Concurrent Deals', value: phase3.concurrentDeals, target: '2+', met: phase3.concurrentDeals >= 2, icon: Target },
    { label: 'Logins This Week', value: phase3.loginsThisWeek, target: '2–3', met: phase3.loginsThisWeek >= 2, icon: Activity },
    { label: 'Deal Progressed to CLOSd', value: phase3.dealsClosed > 0 ? 'Yes' : 'No', target: '1+', met: phase3.dealsClosed > 0, icon: CheckCircle },
    { label: 'Customizations Made', value: phase3.customizations, target: 'Any', met: phase3.customizations > 0, icon: BarChart3 },
    { label: 'Team Collaboration', value: phase3.uniqueTaskCompleters > 1 ? `${phase3.uniqueTaskCompleters} people` : 'No', target: '2+ people', met: phase3.uniqueTaskCompleters > 1, icon: Users },
    { label: 'Live Deals Actively Managed', value: phase3.concurrentDeals, target: '2+', met: phase3.concurrentDeals >= 2, icon: Zap },
  ];

  // Day 42 checkpoint
  const day42Met = phase3.concurrentDeals >= 2;
  const showDay42Alert = ageDays >= 42 && !day42Met;

  return (
    <div className="bg-white rounded-lg shadow p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg" style={{ backgroundColor: '#75BB2E15' }}>
            <Target className="w-5 h-5" style={{ color: '#75BB2E' }} />
          </div>
          <div>
            <h3 className="font-bold" style={{ color: '#071D39' }}>Phase 3 — Dependency</h3>
            <p className="text-xs" style={{ color: '#516469' }}>Weeks 4–6 · They can't live without it</p>
          </div>
        </div>
      </div>

      {/* Day 42 Checkpoint Alert */}
      {showDay42Alert && (
        <div
          className="flex items-center gap-3 p-3 rounded-lg mb-4"
          style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }}
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: '#DC2626' }} />
          <div>
            <p className="text-sm font-bold" style={{ color: '#DC2626' }}>
              🚨 Day 42 Checkpoint MISSED
            </p>
            <p className="text-xs" style={{ color: '#DC2626' }}>
              This org should have 2+ live deals actively being managed by Day 42. They have {phase3.concurrentDeals} concurrent deal(s).
            </p>
          </div>
        </div>
      )}

      {ageDays < 42 && ageDays >= 28 && (
        <div
          className="flex items-center gap-3 p-3 rounded-lg mb-4"
          style={{ backgroundColor: '#EBF8FF', border: '1px solid #BEE3F8' }}
        >
          <Clock className="w-5 h-5 flex-shrink-0" style={{ color: '#2B6CB0' }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#2B6CB0' }}>
              Day 42 Checkpoint in {42 - ageDays} day(s)
            </p>
            <p className="text-xs" style={{ color: '#2B6CB0' }}>
              Target: 2+ live deals actively being managed
            </p>
          </div>
        </div>
      )}

      {ageDays >= 42 && day42Met && (
        <div
          className="flex items-center gap-3 p-3 rounded-lg mb-4"
          style={{ backgroundColor: '#F0FFF4', border: '1px solid #C6F6D5' }}
        >
          <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#38A169' }} />
          <p className="text-sm font-bold" style={{ color: '#38A169' }}>
            ✅ Day 42 Checkpoint PASSED
          </p>
        </div>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-3">
        {metrics.map((m, idx) => {
          const Icon = m.icon;
          return (
            <div
              key={idx}
              className="p-3 rounded-lg"
              style={{
                backgroundColor: m.met ? '#F0FFF4' : '#FFF5F5',
                border: `1px solid ${m.met ? '#C6F6D5' : '#FED7D7'}`,
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" style={{ color: m.met ? '#38A169' : '#E53E3E' }} />
                <span className="text-xs font-semibold" style={{ color: '#516469' }}>{m.label}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xl font-bold" style={{ color: '#071D39' }}>{m.value}</span>
                <span className="text-xs px-2 py-0.5 rounded" style={{
                  backgroundColor: m.met ? '#C6F6D5' : '#FED7D7',
                  color: m.met ? '#38A169' : '#E53E3E',
                }}>
                  Target: {m.target}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// HEALTH BADGE
// ============================================

const HealthBadge = ({ data }) => {
  const { phase1, phase2, phase3, phase, ageDays } = data;

  // Calculate overall health
  const p1Score = [phase1.accountCreated, phase1.firstDealCreated, phase1.firstChecklistTriggered, phase1.firstTaskCompleted, phase1.firstDueDateSet].filter(Boolean).length;
  const p1Pct = p1Score / 5;

  let status = 'on-track';
  let label = 'On Track';
  let color = '#75BB2E';

  // Phase 1 checks
  if (phase === 1 && ageDays > 3 && p1Pct < 0.4) {
    status = 'at-risk';
    label = 'At Risk';
    color = '#D69E2E';
  }
  if (phase === 1 && ageDays > 5 && p1Pct < 0.6) {
    status = 'at-risk';
    label = 'At Risk';
    color = '#D69E2E';
  }

  // Phase 2 checks
  if (phase >= 2 && ageDays >= 14 && (phase2.activeDeals < 1 || phase2.tasksCompleted < 5)) {
    status = 'needs-attention';
    label = 'Needs Attention';
    color = '#E53E3E';
  } else if (phase >= 2 && phase2.daysActiveThisWeek < 2) {
    status = 'at-risk';
    label = 'At Risk';
    color = '#D69E2E';
  }

  // Phase 3 checks
  if (phase >= 3 && ageDays >= 42 && phase3.concurrentDeals < 2) {
    status = 'needs-attention';
    label = 'Needs Attention';
    color = '#E53E3E';
  } else if (phase >= 3 && phase3.loginsThisWeek < 2) { // eslint-disable-next-line no-unused-vars
    status = 'at-risk';
    label = 'At Risk';
    color = '#D69E2E';
  }

  // If everything looks good in later phases, upgrade to "Thriving"
  if (phase >= 3 && phase3.concurrentDeals >= 2 && phase3.loginsThisWeek >= 2 && phase3.dealsClosed > 0) {
    label = '🌟 Thriving';
    color = '#75BB2E';
  }

  return (
    <span
      className="px-3 py-1.5 rounded-lg text-sm font-bold"
      style={{ backgroundColor: color + '15', color: color, border: `2px solid ${color}` }}
    >
      {label}
    </span>
  );
};

// ============================================
// PHASE TAB (timeline indicator)
// ============================================

const PhaseTab = ({ num, label, days, active, completed, color }) => {
  const bg = active ? color + '15' : completed ? '#F0FFF4' : '#F7FAFC';
  const border = active ? color : completed ? '#C6F6D5' : '#E2E8F0';
  const textColor = active ? color : completed ? '#38A169' : '#89A8B1';

  return (
    <div
      className="flex-1 px-3 py-2 rounded-lg text-center"
      style={{ backgroundColor: bg, border: `2px solid ${border}` }}
    >
      <p className="text-xs font-bold" style={{ color: textColor }}>
        {completed && '✓ '}Phase {num}
      </p>
      <p className="text-xs font-semibold" style={{ color: textColor }}>{label}</p>
      <p className="text-xs" style={{ color: '#89A8B1' }}>{days}</p>
    </div>
  );
};

// ============================================
// COMPUTE KPIs FROM RAW DATA
// ============================================

const getOrgAgeDays = (org) => {
  const createdAt = org?.createdAt?.toDate
    ? org.createdAt.toDate()
    : new Date(org?.createdAt || Date.now());
  return Math.floor((new Date() - createdAt) / (1000 * 60 * 60 * 24));
};

const computeKPIs = (logs, projects, members, createdAt) => {
  const now = new Date();
  const ageDays = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
  const phase = ageDays <= 7 ? 1 : ageDays <= 21 ? 2 : 3;

  // ---- PHASE 1 ----
  const accountCreated = true;

  // Check BOTH activity logs AND actual Firestore data (covers pre-logging history)
  const dealCreatedLogs = logs.filter(l => l.type === 'dealCreated');
  const firstDealCreated = dealCreatedLogs.length > 0 || projects.length > 0;
  let firstDealWithin48hrs = false;
  if (firstDealCreated) {
    if (dealCreatedLogs.length > 0) {
      const firstDealTime = dealCreatedLogs[0].timestamp?.toDate
        ? dealCreatedLogs[0].timestamp.toDate()
        : new Date(dealCreatedLogs[0].timestamp);
      const hoursDiff = (firstDealTime - createdAt) / (1000 * 60 * 60);
      firstDealWithin48hrs = hoursDiff <= 48;
    } else if (projects.length > 0) {
      // Fallback: check the earliest project's createdAt
      const earliest = projects.reduce((min, p) => {
        const pDate = p.createdAt?.toDate ? p.createdAt.toDate() : new Date(p.createdAt || Date.now());
        return pDate < min ? pDate : min;
      }, new Date());
      const hoursDiff = (earliest - createdAt) / (1000 * 60 * 60);
      firstDealWithin48hrs = hoursDiff <= 48;
    }
  }

  // First checklist triggered = deal exists with tasks
  const firstChecklistTriggered = projects.some(p => p.tasks && p.tasks.length > 0);

  const taskCompletedLogs = logs.filter(l => l.type === 'taskCompleted');
  const hasCompletedTaskInFirestore = projects.some(p =>
    p.tasks?.some(t => t.status === 'complete')
  );
  const firstTaskCompleted = taskCompletedLogs.length > 0 || hasCompletedTaskInFirestore;

  const dueDateLogs = logs.filter(l => l.type === 'dueDateSet');
  const hasTaskWithDueDate = projects.some(p =>
    p.tasks?.some(t => t.dueDate)
  );
  const firstDueDateSet = dueDateLogs.length > 0 || hasTaskWithDueDate;

  // ---- PHASE 2 ----
  const activeDeals = projects.filter(p => p.status === 'active').length;
  const tasksCompleted = taskCompletedLogs.length > 0
    ? taskCompletedLogs.length
    : projects.reduce((sum, p) => sum + (p.tasks?.filter(t => t.status === 'complete').length || 0), 0);

  const totalTasks = projects.reduce((sum, p) => sum + (p.tasks?.length || 0), 0);
  const completedTasks = projects.reduce((sum, p) => {
    return sum + (p.tasks?.filter(t => t.status === 'complete').length || 0);
  }, 0);
  const checklistPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Days active this week (distinct days with a login log in the past 7 days)
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const loginLogs = logs.filter(l => l.type === 'login');
  const recentLogins = loginLogs.filter(l => {
    const ts = l.timestamp?.toDate ? l.timestamp.toDate() : new Date(l.timestamp);
    return ts >= sevenDaysAgo;
  });
  const uniqueDays = new Set(
    recentLogins.map(l => {
      const ts = l.timestamp?.toDate ? l.timestamp.toDate() : new Date(l.timestamp);
      return ts.toDateString();
    })
  );
  const daysActiveThisWeek = uniqueDays.size;

  const seatAddedLogs = logs.filter(l => l.type === 'seatAdded');
  const seatsAdded = seatAddedLogs.length;

  // ---- PHASE 3 ----
  const concurrentDeals = activeDeals;

  // Logins this week
  const loginsThisWeek = recentLogins.length;

  // Deals closed
  const dealClosedLogs = logs.filter(l => l.type === 'dealClosed');
  const dealsClosed = dealClosedLogs.length;

  // Customizations
  const customizationLogs = logs.filter(l => l.type === 'taskCustomized');
  const customizations = customizationLogs.length;

  // Team collaboration — unique users who have completed tasks
  const uniqueTaskCompleters = new Set(taskCompletedLogs.map(l => l.userId)).size;

  return {
    ageDays,
    phase,
    phase1: {
      accountCreated,
      firstDealCreated,
      firstDealWithin48hrs,
      firstChecklistTriggered,
      firstTaskCompleted,
      firstDueDateSet,
    },
    phase2: {
      activeDeals,
      tasksCompleted,
      checklistPct,
      daysActiveThisWeek,
      seatsAdded,
    },
    phase3: {
      concurrentDeals,
      loginsThisWeek,
      dealsClosed,
      customizations,
      uniqueTaskCompleters,
    },
  };
};

export default ClientKPIDashboard;